import { Link } from "react-router-dom";
import { useSiteFaqs } from "@/pages/SitePages";
import { Award, CheckCircle2, Circle, Clock, Flame, GraduationCap, Headphones, Lightbulb, Sparkles, Briefcase, RefreshCw, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { EmailLink } from "@/components/EmailLink";

const Eyebrow = ({ children }) => <p className="font-mono-label mb-3 text-xs uppercase tracking-[0.2em] text-[#222d99]">{children}</p>;

const SAMPLE_COURSES = [
  { title: "Customer Support Foundations", level: "Beginner", hours: 6, price: "Free", icon: Headphones },
  { title: "Workplace Communication Essentials", level: "Beginner", hours: 4, price: "Free", icon: Lightbulb },
  { title: "Spreadsheet Skills for the Workplace", level: "Intermediate", hours: 12, price: "₦15,000", icon: GraduationCap },
  { title: "Interview Confidence Bootcamp", level: "Beginner", hours: 3, price: "Free", icon: Sparkles },
];

export function FeaturedCourses() {
  return (
    <section id="courses" className="scroll-mt-20 border-b border-slate-200 bg-[#F8FAFC]">
      <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:py-24">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <Eyebrow>Featured courses</Eyebrow>
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Start with practical, job-focused courses</h2>
          </div>
          <span className="w-fit rounded-full border border-amber-400 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-900" data-testid="featured-sample-label">Sample content</span>
        </div>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="featured-courses">
          {SAMPLE_COURSES.map(({ title, level, hours, price, icon: Icon }, i) => (
            <article key={title} className="flex flex-col rounded-2xl border border-slate-200 bg-white p-5 transition-shadow hover:shadow-md" data-testid={`featured-course-${i}`}>
              <div className="grid h-11 w-11 place-items-center rounded-xl bg-[#e8eaf6]"><Icon className="h-5 w-5 text-[#222d99]" /></div>
              <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-slate-600">Sample course</p>
              <h3 className="mt-1 font-display text-lg font-bold leading-snug text-slate-900">{title}</h3>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-slate-700">
                <span className="rounded-full border border-slate-300 px-2.5 py-0.5 text-xs">{level}</span>
                <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {hours} hours</span>
              </div>
              <p className={`mt-auto pt-4 font-display text-xl font-extrabold ${price === "Free" ? "text-emerald-700" : "text-slate-900"}`} data-testid={`featured-course-price-${i}`}>{price}</p>
              <Button asChild className="mt-4 rounded-full bg-[#222d99] hover:bg-[#1a237e]" data-testid={`featured-course-start-${i}`}><Link to="/sign-up">Start</Link></Button>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

const AUDIENCES = [
  { icon: Search, title: "Job seekers", body: "Learn the skills listed in the roles you're applying for and show proof on your profile." },
  { icon: Briefcase, title: "Unemployed professionals", body: "Refresh your skills, fill gaps and get back into work with confidence." },
  { icon: GraduationCap, title: "Recent graduates", body: "Turn your degree into job-ready skills employers can see and trust." },
  { icon: RefreshCw, title: "Career changers", body: "Move into a new field step by step, around the time you have each week." },
];

export function WhoItsFor() {
  return (
    <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:py-24" data-testid="who-its-for">
      <Eyebrow>Who it's for</Eyebrow>
      <h2 className="max-w-2xl text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Wherever you're starting from, there's a path for you.</h2>
      <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {AUDIENCES.map(({ icon: Icon, title, body }) => (
          <div key={title} className="border-l-4 border-[#222d99] bg-white py-2 pl-5">
            <Icon className="h-5 w-5 text-[#222d99]" />
            <h3 className="mt-3 text-lg font-semibold text-slate-900">{title}</h3>
            <p className="mt-1 text-sm leading-relaxed text-slate-700">{body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

const PATH = [
  { title: "Customer Support Foundations", state: "done" },
  { title: "Workplace Communication Essentials", state: "current" },
  { title: "Practical project: rewrite three replies", state: "next" },
  { title: "Interview Confidence Bootcamp", state: "next" },
];
const BADGES = [{ icon: GraduationCap, label: "First course" }, { icon: Flame, label: "7-day streak" }, { icon: Award, label: "Top score" }];

export function DashboardPreview() {
  return (
    <section className="bg-[#222d99]" data-testid="dashboard-preview">
      <div className="mx-auto grid max-w-6xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-12 lg:py-24">
        <div className="lg:col-span-5">
          <p className="font-mono-label mb-3 text-xs uppercase tracking-[0.2em] text-indigo-100">Your dashboard</p>
          <h2 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">See your path, your progress and what to do next.</h2>
          <p className="mt-4 text-base text-indigo-50">Your dashboard shows the next lesson to take, how far you've come and the milestones you've earned — all in one lightweight screen.</p>
        </div>
        <div className="lg:col-span-7">
          <div className="rounded-2xl border border-white/20 bg-white p-5 shadow-2xl sm:p-6" aria-label="Illustration of the learner dashboard">
            <div className="flex items-center justify-between">
              <p className="font-display text-lg font-bold text-slate-900">Learning path: Customer Support Associate</p>
              <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-semibold text-slate-700">Illustration</span>
            </div>
            <div className="mt-4">
              <div className="flex justify-between text-sm text-slate-700"><span>Path progress</span><span className="font-semibold">40%</span></div>
              <div className="mt-2 h-2 rounded-full bg-slate-200"><div className="h-2 w-2/5 rounded-full bg-[#222d99]" /></div>
            </div>
            <ol className="mt-5 space-y-2">
              {PATH.map((p) => (
                <li key={p.title} className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 text-sm ${p.state === "current" ? "border-[#222d99] bg-[#e8eaf6] font-semibold text-[#222d99]" : "border-slate-200 text-slate-800"}`}>
                  {p.state === "done" ? <CheckCircle2 className="h-4 w-4 text-emerald-700" /> : <Circle className={`h-4 w-4 ${p.state === "current" ? "text-[#222d99]" : "text-slate-500"}`} />}
                  <span className="flex-1">{p.title}</span>
                  {p.state === "current" && <span className="text-xs">Up next</span>}
                </li>
              ))}
            </ol>
            <div className="mt-5 flex flex-wrap gap-2">
              {BADGES.map(({ icon: Icon, label }) => (
                <span key={label} className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-3 py-1 text-xs font-medium text-slate-800"><Icon className="h-3.5 w-3.5 text-[#222d99]" /> {label}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export const FAQS = [
  { q: "Is it free?", a: "Creating an account is free and many courses are free. Some courses are paid — the price is always shown before you enrol." },
  { q: "How do assessments work?", a: "Assessments are short tests on specific skills. You'll see your score by skill area, your strengths and gaps. Passing an assessment marks that skill as assessed on your profile." },
  { q: "Who can see my profile?", a: "Nobody but you, unless you choose otherwise. Profiles are private by default. If you make yours public, only approved employers can view it, and only the evidence you choose to share." },
  { q: "How do employers find me?", a: "Approved employers can search public profiles by skills and verification level. If one is interested, they send a message through Crowlearn — your contact details stay hidden until you accept." },
];

export function Faq() {
  const faqs = useSiteFaqs(FAQS);
  return (
    <section id="faq" className="mx-auto max-w-3xl scroll-mt-20 px-4 py-16 sm:px-6 lg:py-24" data-testid="faq-section">
      <Eyebrow>FAQ</Eyebrow>
      <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Common questions</h2>
      <Accordion type="single" collapsible className="mt-8">
        {faqs.map((f, i) => (
          <AccordionItem key={f.q} value={`faq-${i}`}>
            <AccordionTrigger className="text-left text-base font-semibold text-slate-900" data-testid={`faq-question-${i}`}>{f.q}</AccordionTrigger>
            <AccordionContent className="text-base leading-relaxed text-slate-700" data-testid={`faq-answer-${i}`}>{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      <p className="mt-8 text-base text-slate-700" data-testid="faq-email-line">Can't find an answer? Email <EmailLink testid="faq-email" /></p>
    </section>
  );
}
